/**
 * «Base di conoscenza» del Support Hub (Task 29): pagina condivisa fra il
 * Centro Ticket admin (`/admin/centro-ticket/conoscenza`) e l'area
 * rappresentante (`/area-associativa/supporto/conoscenza`).
 *
 * Le schede nascono in BOZZA dai ticket risolti (o a mano) ed entrano nei
 * «casi simili» di «Proponi una risposta» solo dopo l'approvazione. Qui si
 * correggono, si approvano, si archiviano e si ripristinano.
 *
 * `variant` cambia solo il vestito delle card (come NotificationPreferences):
 * `a2-card` per l'admin V2, classi Tailwind della shell per l'area associativa.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Archive, BookMarked, CheckCircle2, ExternalLink, Loader2, Plus, RefreshCw, RotateCcw, Search, Trash2, X } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import ConfirmDialog from '../ConfirmDialog';
import type { KnowledgeApi, KnowledgeCard, KnowledgeListResult, KnowledgeStatus, KnowledgeWriteResult } from '../../services/supportKnowledgeApi';

export type KnowledgeVariant = 'admin' | 'associative';

export interface KnowledgeBaseProps {
  api: KnowledgeApi;
  /** Correggere, approvare, archiviare, creare (`*.manage`). */
  canManage: boolean;
  variant?: KnowledgeVariant;
  /** Scheda da aprire subito (link «Casi simili usati», `?scheda=`). */
  initialCardId?: string | null;
}

const CARD_CLASS: Record<KnowledgeVariant, string> = {
  admin: 'a2-card p-4 sm:p-5',
  associative: 'rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 sm:p-5',
};

const TEXT = 'text-sm text-gray-700 dark:text-gray-200';
const MUTED = 'text-xs text-gray-500 dark:text-gray-400';
const INPUT =
  'w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-800 dark:text-gray-100 px-2 py-1.5 disabled:opacity-70';
const BTN =
  'inline-flex items-center gap-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 text-sm px-3 py-1.5';
const BTN_PRIMARY =
  'inline-flex items-center gap-1.5 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm px-3 py-1.5';
const BTN_GREEN =
  'inline-flex items-center gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm px-3 py-1.5';

export const TABS: { status: KnowledgeStatus; label: string }[] = [
  { status: 'draft', label: 'Bozze' },
  { status: 'approved', label: 'Approvate' },
  { status: 'archived', label: 'Archiviate' },
];

const STATUS_LABEL: Record<KnowledgeStatus, string> = { draft: 'Bozza', approved: 'Approvata', archived: 'Archiviata' };

const EMPTY_COUNTS: Record<KnowledgeStatus, number> = { draft: 0, approved: 0, archived: 0 };

interface FormState {
  title: string;
  question: string;
  answer: string;
  tags: string;
  groupId: string;
}

const toForm = (c?: KnowledgeCard | null): FormState => ({
  title: c?.title || '',
  question: c?.question || '',
  answer: c?.answer || '',
  tags: (c?.tags || []).join(', '),
  groupId: c?.groupId || '',
});

/** Tag dal campo di testo: separati da virgola, minuscoli, senza doppioni. */
export const parseTags = (raw: string): string[] =>
  Array.from(new Set(raw.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean))).slice(0, 8);

const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString('it-IT') : '');

const errMsg = (e: unknown, fallback: string): string => {
  const data = (e as { response?: { data?: { error?: string } } })?.response?.data;
  return (data && data.error) || fallback;
};

const KnowledgeBase: React.FC<KnowledgeBaseProps> = ({ api, canManage, variant = 'admin', initialCardId }) => {
  const toast = useToast();
  const card = CARD_CLASS[variant];

  const [status, setStatus] = useState<KnowledgeStatus>('draft');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [groupId, setGroupId] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<KnowledgeListResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [groups, setGroups] = useState<{ _id: string; nome: string }[]>([]);

  // Pannello: scheda aperta (o nuova scheda se `creating`).
  const [open, setOpen] = useState<KnowledgeCard | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<FormState>(toForm());
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Ricerca con un piccolo ritardo: niente richiesta a ogni tasto.
  useEffect(() => {
    const t = setTimeout(() => { setQuery(q.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api.list({ status, q: query || undefined, groupId: groupId || undefined, page, limit: 20 });
      setData(res);
    } catch (e) {
      setLoadError(errMsg(e, 'Impossibile caricare le schede.'));
    } finally {
      setLoading(false);
    }
  }, [api, status, query, groupId, page]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.groups().then(setGroups).catch(() => setGroups([]));
  }, [api]);

  const openCard = useCallback((c: KnowledgeCard) => {
    setCreating(false);
    setOpen(c);
    setForm(toForm(c));
  }, []);

  // Link «Casi simili usati» → `?scheda=<id>`: si apre la scheda richiesta.
  const openedInitial = useRef<string | null>(null);
  useEffect(() => {
    if (!initialCardId || openedInitial.current === initialCardId) return;
    openedInitial.current = initialCardId;
    api.get(initialCardId)
      .then((c) => { setStatus(c.status); openCard(c); })
      .catch((e) => toast.error(errMsg(e, 'Scheda non trovata')));
  }, [api, initialCardId, openCard, toast]);

  const closePanel = () => {
    setOpen(null);
    setCreating(false);
  };

  const startCreate = () => {
    setOpen(null);
    setCreating(true);
    setForm({ ...toForm(), groupId: groupId || (groups.length === 1 ? groups[0]._id : '') });
  };

  const dirty = open
    ? form.title !== open.title ||
      form.question !== open.question ||
      form.answer !== open.answer ||
      parseTags(form.tags).join(',') !== (open.tags || []).join(',') ||
      (form.groupId || '') !== (open.groupId || '')
    : false;

  const formError = (): string | null => {
    if (!form.title.trim()) return 'Il titolo è obbligatorio.';
    if (form.title.trim().length > 160) return 'Il titolo può avere al massimo 160 caratteri.';
    if (!form.question.trim()) return 'La domanda è obbligatoria.';
    if (!form.answer.trim()) return 'La risposta è obbligatoria.';
    if (creating && !form.groupId) return 'Scegli il canale.';
    return null;
  };

  const payload = () => ({
    title: form.title.trim(),
    question: form.question.trim(),
    answer: form.answer.trim(),
    tags: parseTags(form.tags),
    ...(form.groupId ? { groupId: form.groupId } : {}),
  });

  /** Dopo una scrittura: avviso (embedding), scheda aggiornata nel pannello, lista ricaricata. */
  const applied = async (r: KnowledgeWriteResult, ok: string) => {
    if (r.warning) toast.warning(r.warning);
    else toast.success(ok);
    setCreating(false);
    setOpen(r.card);
    setForm(toForm(r.card));
    await load();
  };

  const run = async (fn: () => Promise<KnowledgeWriteResult>, ok: string, fallback: string) => {
    setBusy(true);
    try {
      await applied(await fn(), ok);
    } catch (e) {
      toast.error(errMsg(e, fallback));
    } finally {
      setBusy(false);
    }
  };

  const save = () => {
    const err = formError();
    if (err) { toast.warning(err); return; }
    if (creating) return run(() => api.create({ ...payload(), groupId: form.groupId }), 'Scheda creata come bozza', 'Errore creazione scheda');
    if (!open) return;
    return run(() => api.update(open._id, payload()), 'Scheda salvata', 'Errore salvataggio scheda');
  };

  const createApproved = () => {
    const err = formError();
    if (err) { toast.warning(err); return; }
    return run(() => api.create({ ...payload(), groupId: form.groupId }, true), 'Scheda creata e approvata', 'Errore creazione scheda');
  };

  // Approva: le modifiche non salvate si salvano prima, cosi non si perdono.
  const approve = () => {
    if (!open) return;
    const err = formError();
    if (err) { toast.warning(err); return; }
    const id = open._id;
    return run(async () => {
      if (dirty) await api.update(id, payload());
      return api.approve(id);
    }, 'Scheda approvata: ora la usa «Proponi una risposta»', 'Errore approvazione');
  };

  const archive = () => open && run(() => api.archive(open._id), 'Scheda archiviata', 'Errore archiviazione');
  const restore = () => open && run(() => api.restore(open._id), 'Scheda ripristinata come bozza', 'Errore ripristino');
  const regenerate = () =>
    open?.sourceTicketId &&
    run(() => api.generateFromTicket(open.sourceTicketId as string), 'Bozza rigenerata dal ticket', 'Errore rigenerazione');

  const doDelete = async () => {
    setConfirmDelete(false);
    if (!open || !api.remove) return;
    setBusy(true);
    try {
      await api.remove(open._id);
      toast.success('Scheda eliminata');
      closePanel();
      await load();
    } catch (e) {
      toast.error(errMsg(e, 'Errore eliminazione'));
    } finally {
      setBusy(false);
    }
  };

  // Esc chiude il pannello.
  const panelOpen = Boolean(open) || creating;
  useEffect(() => {
    if (!panelOpen) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(null); setCreating(false); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [panelOpen]);

  const counts = data?.counts || EMPTY_COUNTS;
  const readOnly = !canManage;

  return (
    <div className="max-w-5xl mx-auto space-y-4">
      <div className={card}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <BookMarked size={16} aria-hidden="true" /> Base di conoscenza
            </h1>
            <p className={`${TEXT} mt-1`}>
              Le schede dei casi già risolti. Nascono come bozze dai ticket risolti e, una volta approvate,
              «Proponi una risposta» le usa come casi simili.
            </p>
          </div>
          {canManage && (
            <button type="button" className={BTN_PRIMARY} onClick={startCreate}>
              <Plus size={14} aria-hidden="true" /> Nuova scheda
            </button>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div role="tablist" aria-label="Stato delle schede" className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 p-0.5">
            {TABS.map((t) => {
              const on = t.status === status;
              return (
                <button
                  key={t.status}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => { setStatus(t.status); setPage(1); }}
                  className={`text-sm px-3 py-1 rounded-md inline-flex items-center gap-1.5 ${
                    on ? 'bg-blue-600 text-white' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {t.label}
                  <span className={`text-xs ${on ? 'text-white/90' : 'text-gray-500 dark:text-gray-400'}`}>{counts[t.status]}</span>
                  {t.status === 'draft' && counts.draft > 0 && (
                    <span className="text-2xs font-semibold uppercase tracking-wide rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 px-1.5 py-0.5">
                      Da approvare
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <label className="relative flex-1 min-w-[12rem]">
            <span className="sr-only">Cerca nelle schede</span>
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
            <input
              type="search"
              className={`${INPUT} pl-7`}
              placeholder="Cerca per titolo, testo, tag o numero ticket"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </label>

          {groups.length > 1 && (
            <select
              aria-label="Filtra per canale"
              className={`${INPUT} w-auto`}
              value={groupId}
              onChange={(e) => { setGroupId(e.target.value); setPage(1); }}
            >
              <option value="">Tutti i canali</option>
              {groups.map((g) => <option key={g._id} value={g._id}>{g.nome}</option>)}
            </select>
          )}
        </div>
      </div>

      <div className={card}>
        {loadError ? (
          <div role="alert">
            <p className="text-sm text-red-600 dark:text-red-400">{loadError}</p>
            <button type="button" onClick={load} className="mt-2 text-sm underline text-gray-700 dark:text-gray-200">Riprova</button>
          </div>
        ) : loading && !data ? (
          <p className={`${TEXT} flex items-center gap-2`}><Loader2 size={14} className="animate-spin" aria-hidden="true" /> Caricamento…</p>
        ) : !data || data.items.length === 0 ? (
          <p className={TEXT}>
            {status === 'draft'
              ? 'Nessuna bozza da approvare. Le bozze arrivano da sole quando un ticket con una risposta del canale viene risolto.'
              : status === 'approved'
                ? 'Nessuna scheda approvata.'
                : 'Nessuna scheda archiviata.'}
          </p>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700" aria-label="Schede">
            {data.items.map((c) => (
              <li key={c._id}>
                <button
                  type="button"
                  onClick={() => openCard(c)}
                  className="w-full text-left py-2.5 px-1 hover:bg-gray-50 dark:hover:bg-gray-700/40 rounded-md"
                >
                  <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{c.title}</span>
                  <span className={`block ${MUTED} mt-0.5`}>
                    {[
                      c.groupName,
                      c.sourceTicketNumber ? `ticket #${c.sourceTicketNumber}` : 'scritta a mano',
                      `aggiornata il ${fmtDate(c.updatedAt)}`,
                      c.status === 'approved' ? `usata ${c.usageCount} ${c.usageCount === 1 ? 'volta' : 'volte'}` : null,
                    ].filter(Boolean).join(' · ')}
                  </span>
                  {c.tags.length > 0 && (
                    <span className="flex flex-wrap gap-1 mt-1">
                      {c.tags.map((t) => (
                        <span key={t} className="text-2xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-1.5 py-0.5">{t}</span>
                      ))}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}

        {data && data.totalPages > 1 && (
          <div className="mt-3 flex items-center justify-between">
            <button type="button" className={BTN} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Precedenti</button>
            <span className={MUTED}>Pagina {data.page} di {data.totalPages}</span>
            <button type="button" className={BTN} disabled={page >= data.totalPages} onClick={() => setPage((p) => p + 1)}>Successive</button>
          </div>
        )}
      </div>

      {panelOpen && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={closePanel}>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="kb-panel-title"
            className="h-full w-full max-w-xl overflow-y-auto bg-white dark:bg-gray-800 shadow-xl p-4 sm:p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h2 id="kb-panel-title" className="text-base font-semibold text-gray-900 dark:text-gray-100">
                  {creating ? 'Nuova scheda' : 'Scheda di conoscenza'}
                </h2>
                {open && (
                  <p className={`${MUTED} mt-0.5`}>
                    {STATUS_LABEL[open.status]}
                    {open.groupName ? ` · ${open.groupName}` : ''}
                    {open.status === 'approved' && !open.embedded ? ' · trovata solo per parole chiave' : ''}
                  </p>
                )}
              </div>
              <button type="button" className="p-1 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700" aria-label="Chiudi" onClick={closePanel}>
                <X size={16} />
              </button>
            </div>

            {open?.sourceTicketNumber && (
              <p className={`${TEXT} mt-3`}>
                Dal ticket{' '}
                {open.sourceTicketAccessible && open.sourceTicketId ? (
                  <Link to={api.ticketPath(open.sourceTicketId)} className="underline inline-flex items-center gap-1">
                    #{open.sourceTicketNumber} <ExternalLink size={12} aria-hidden="true" />
                  </Link>
                ) : (
                  <span>#{open.sourceTicketNumber}</span>
                )}
              </p>
            )}

            <div className="mt-3 space-y-3">
              <label className="block">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Titolo (la domanda in breve)</span>
                <input className={`${INPUT} mt-1`} value={form.title} maxLength={160} disabled={readOnly || busy}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Domanda</span>
                <textarea className={`${INPUT} mt-1`} rows={4} value={form.question} disabled={readOnly || busy}
                  onChange={(e) => setForm((f) => ({ ...f, question: e.target.value }))} />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Risposta</span>
                <textarea className={`${INPUT} mt-1`} rows={9} value={form.answer} disabled={readOnly || busy}
                  onChange={(e) => setForm((f) => ({ ...f, answer: e.target.value }))} />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Tag (separati da virgola)</span>
                <input className={`${INPUT} mt-1`} value={form.tags} disabled={readOnly || busy}
                  onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} />
              </label>
              {(creating || (canManage && groups.length > 1)) && (
                <label className="block">
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Canale</span>
                  <select className={`${INPUT} mt-1`} value={form.groupId} disabled={readOnly || busy}
                    onChange={(e) => setForm((f) => ({ ...f, groupId: e.target.value }))}>
                    {creating && <option value="">Scegli il canale…</option>}
                    {groups.map((g) => <option key={g._id} value={g._id}>{g.nome}</option>)}
                  </select>
                </label>
              )}
            </div>

            {open && (
              <p className={`${MUTED} mt-3`}>
                {open.createdBy?.actorType === 'system' ? 'Bozza generata dall’assistente AI' : `Creata da ${open.createdBy?.nome || 'un operatore'}`}
                {open.approvedBy && open.status === 'approved' ? ` · approvata da ${open.approvedBy.nome || 'un operatore'} il ${fmtDate(open.approvedAt)}` : ''}
                {open.updatedBy?.nome ? ` · ultima modifica di ${open.updatedBy.nome}` : ''}
                {open.status === 'approved' ? ` · usata ${open.usageCount} ${open.usageCount === 1 ? 'volta' : 'volte'}` : ''}
              </p>
            )}

            {canManage && (
              <div className="mt-4 flex flex-wrap gap-2">
                {creating ? (
                  <>
                    <button type="button" className={BTN_PRIMARY} disabled={busy} onClick={save}>
                      {busy && <Loader2 size={13} className="animate-spin" aria-hidden="true" />} Salva come bozza
                    </button>
                    <button type="button" className={BTN_GREEN} disabled={busy} onClick={createApproved}>
                      <CheckCircle2 size={13} aria-hidden="true" /> Salva e approva
                    </button>
                  </>
                ) : open && (
                  <>
                    <button type="button" className={BTN_PRIMARY} disabled={busy || !dirty} onClick={save}>
                      {busy && <Loader2 size={13} className="animate-spin" aria-hidden="true" />} Salva
                    </button>
                    {open.status === 'draft' && (
                      <button type="button" className={BTN_GREEN} disabled={busy} onClick={approve}>
                        <CheckCircle2 size={13} aria-hidden="true" /> Approva
                      </button>
                    )}
                    {open.status !== 'archived' && (
                      <button type="button" className={BTN} disabled={busy} onClick={archive}>
                        <Archive size={13} aria-hidden="true" /> Archivia
                      </button>
                    )}
                    {open.status === 'archived' && (
                      <button type="button" className={BTN} disabled={busy} onClick={restore}>
                        <RotateCcw size={13} aria-hidden="true" /> Ripristina
                      </button>
                    )}
                    {open.status === 'draft' && open.sourceTicketId && (
                      <button type="button" className={BTN} disabled={busy} onClick={regenerate} title="Rigenera la bozza dall’assistente AI: sostituisce il testo attuale">
                        <RefreshCw size={13} aria-hidden="true" /> Rigenera dal ticket
                      </button>
                    )}
                    {api.remove && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => setConfirmDelete(true)}
                        className="inline-flex items-center gap-1.5 rounded-md border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 text-sm px-3 py-1.5"
                      >
                        <Trash2 size={13} aria-hidden="true" /> Elimina
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
            {canManage && open?.status === 'draft' && (
              <p className={`${MUTED} mt-2`}>
                Prima di approvare controlla che la scheda non contenga nomi, email, numeri di pratica o altri dati personali.
              </p>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={doDelete}
        title="Eliminare la scheda?"
        message="La scheda viene eliminata per sempre e non sarà più usata come caso simile. Per toglierla solo dalle ricerche usa «Archivia»."
        confirmText="Elimina"
        type="danger"
      />
    </div>
  );
};

export default KnowledgeBase;
